import { useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import RoleplayMessageItem from './RoleplayMessageItem';

const RoleplayConversationArea = ({ messages, newMessageIds, onExit }) => {
  const scrollRef = useRef(null);
  const prevCountRef = useRef(0);
  const isAtBottomRef = useRef(true);

  // Track whether the user is near the bottom before new messages arrive.
  // distFromBottom is measured post-paint (after new content is in the DOM),
  // so we can't compute it inside the messages effect — it would always look
  // like the user is far from the bottom even if they weren't scrolled up.
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const handleScroll = () => {
      isAtBottomRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < 100;
    };
    el.addEventListener('scroll', handleScroll, { passive: true });
    return () => el.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    if (messages.length > prevCountRef.current) {
      // Always scroll to bottom on new messages, deferred until after the
      // browser has painted the updated layout (pinned panel, input area, etc.).
      requestAnimationFrame(() => {
        if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
      });
    }
    prevCountRef.current = messages.length;
  }, [messages.length]);

  // When the pinned panel expands, this container shrinks (flex layout).
  // scrollTop stays fixed while clientHeight decreases, pushing the bottom
  // rows out of view. Re-snap to bottom whenever the container gets smaller.
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const observer = new ResizeObserver(() => {
      if (isAtBottomRef.current) {
        requestAnimationFrame(() => {
          if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
        });
      }
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <div className="rp-conversation-area" ref={scrollRef}>
      <div className="rp-conversation-inner">
        {messages.map((msg) => (
          <RoleplayMessageItem
            key={msg.id}
            msg={msg}
            isNew={newMessageIds ? newMessageIds.has(msg.id) : false}
          />
        ))}
{/* Future feature: debriefing flow after exiting a session
        {onExit && (
          <button className="rp-exit-btn" onClick={onExit} title="End session and debrief">
            <ExitIcon />
          </button>
        )}
*/}
      </div>
    </div>
  );
};

RoleplayConversationArea.propTypes = {
  messages: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      type: PropTypes.string.isRequired,
      text: PropTypes.string.isRequired,
    })
  ).isRequired,
  newMessageIds: PropTypes.instanceOf(Set),
  onExit: PropTypes.func,
};

export default RoleplayConversationArea;
